import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useDocumentAnalysisAPI } from '../DocumentAnalysisAPI';
import DataRoomView from '../portfolio-dashboard/DataRoomView';
import MergeDialog, { MergeSubject } from '../portfolio-dashboard/MergeDialog';
import { missingSlots, requiredSlots } from '../portfolio-dashboard/documentSlots';
import { OpenDecision, useDocumentImport } from '../portfolio-dashboard/documentImport';
import DecisionDialog from '../administration/document-analysis/DecisionDialog';
import {
    defaultTypeKey,
    findStep,
    findType,
    processTypes,
    stepTitle,
} from '../portfolio-dashboard/processModel';
import { wasAborted } from '../utilities/abortedRequests';

/**
 * Die Unterlagen eines Projekts: Ablage und Auskunft nebeneinander, darüber der Prozess.
 *
 * **Warum hier dasselbe wie im Dashboard.** Der Datenraum eines Projekts ist einer, gleich von wo
 * man ihn ansieht. Die abgelöste Liste an dieser Stelle kannte nur die Dateien, die über sie selbst
 * hochgeladen worden waren, und wusste nichts von dem, was die Auswertung über sie festgestellt
 * hat. Zwei Ansichten auf dieselbe Ablage, von denen eine weniger weiß, sind eine zu viel.
 *
 * **Warum nebeneinander und nicht übereinander.** Im Kamin des Dashboards steht die Auskunft unter
 * dem Baum, weil die Spalte schmal ist. Hier ist die volle Breite einer Seite da; nebeneinander
 * behalten beide ihre Höhe, und wer eine Datei anklickt, verliert den Baum nicht aus dem Blick.
 *
 * **Wozu Prozess und Phase.** Sie sagen nicht, welche Dateien zu sehen sind — die Ablage gehört dem
 * Projekt. Sie sagen, **was fehlt**: Jede Phase verlangt bestimmte Unterlagen, und der Vergleich
 * mit dem, was die Auswertung im Raum gefunden hat, steht als Band über der Ablage. Ohne diese
 * beiden Angaben gäbe es keine Frage, gegen die zu prüfen wäre.
 */
interface Properties {
    /** Das Projekt, dessen Unterlagen gezeigt werden. */
    projectId: string;
    /** Sein Name — der Datenraum wird danach angelegt, falls es ihn noch nicht gibt. */
    projectName?: string;
}

export default function ProjectDocumentsTab({ projectId, projectName }: Properties) {
    const { t, i18n } = useTranslation();
    const documentAnalysisApi = useDocumentAnalysisAPI();

    // Derselbe Einstieg wie im Dashboard: der Ankauf, nicht der erste Eintrag der Liste — er ist
    // der Fall, der heute am gründlichsten durchgearbeitet wird.
    const [typeKey, setTypeKey] = useState<string>(defaultTypeKey);
    const [stepKey, setStepKey] = useState<string>('');

    const type = useMemo(() => findType(typeKey), [typeKey]);
    const step = useMemo(() => findStep(type, stepKey), [type, stepKey]);

    const [dataRoomId, setDataRoomId] = useState<string | null>(null);
    const [folderPath, setFolderPath] = useState<string | null>(null);
    const [resolving, setResolving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Der Ordner des Projekts, vom Server aufgelöst — und angelegt, wo es ihn noch nicht gibt.
     *
     * Am Projekt und nicht an der Phase: Ein Mietvertrag wird gegen den ganzen Bestand gelesen. Die
     * Phase bestimmt nur, wogegen geprüft wird.
     */
    useEffect(() => {
        if (!projectId) {
            setDataRoomId(null);
            setFolderPath(null);
            setResolving(false);

            return;
        }

        const abbruch = new AbortController();

        // Der Raum des vorigen Projekts geht sofort — sonst stünde er noch da, während der neue
        // schon geladen wird, und man läse die Unterlagen des einen unter dem Namen des anderen.
        setDataRoomId(null);
        setFolderPath(null);
        setResolving(true);
        setError(null);

        documentAnalysisApi
            .resolveProjectDataRoom(projectId, projectName ?? projectId)
            .then((result: any) => {
                if (abbruch.signal.aborted) return;

                setDataRoomId(result?.dataRoom?._id ?? null);
                setFolderPath(result?.folder?.path ?? null);
            })
            .catch((reason: any) => {
                if (abbruch.signal.aborted || wasAborted(reason)) return;

                setError(reason?.message ?? String(reason));
                setDataRoomId(null);
                setFolderPath(null);
            })
            .finally(() => {
                if (!abbruch.signal.aborted) setResolving(false);
            });

        return () => abbruch.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, projectName]);

    const documentImport = useDocumentImport(dataRoomId, (reason) =>
        console.error('Document import failed', reason),
    );

    /** Was die gewählte Phase verlangt und im Raum noch fehlt — dasselbe Band wie im Dashboard. */
    const missing = useMemo(
        () => missingSlots(requiredSlots(type.key, step.key), documentImport.analyses as any[]),
        [type.key, step.key, documentImport.analyses],
    );

    const [openDecision, setOpenDecision] = useState<OpenDecision | null>(null);
    const [openMerge, setOpenMerge] = useState<MergeSubject | null>(null);

    return (
        <div className="flex min-w-0 flex-col gap-4">
            {/*
              * Prozess und Phase über der Ablage.
              *
              * Nebeneinander, weil die zweite von der ersten abhängt: Ein Prozesstyp bringt seine
              * eigenen Phasen mit, und die Wahl liest sich von links nach rechts.
              */}
            <div className="flex flex-wrap items-end gap-4">
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor="projectDocumentsProcess">
                        {t('portfolioDashboard.types.label')}
                    </Label>
                    <Select
                        value={type.key}
                        onValueChange={(next) => {
                            setTypeKey(next);
                            // Die Phase des alten Typs gibt es im neuen nicht; `findStep` fällt
                            // damit auf dessen erste zurück.
                            setStepKey('');
                        }}
                    >
                        <SelectTrigger id="projectDocumentsProcess" className="w-[260px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {processTypes.map((option) => (
                                <SelectItem key={option.key} value={option.key}>
                                    {t(option.labelKey)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor="projectDocumentsPhase">{t('portfolioDashboard.steps.label')}</Label>
                    <Select value={step.key} onValueChange={setStepKey}>
                        <SelectTrigger id="projectDocumentsPhase" className="w-[260px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {type.steps.map((option) => (
                                <SelectItem key={option.key} value={option.key}>
                                    {/* Zugeschnitten: Die Beschriftungen der Phasen kommen aus dem
                                        Modell, und was dort an Leerraum vorne oder hinten steht,
                                        gehört nicht in eine Auswahl. */}
                                    {stepTitle(option, t, i18n.language ?? 'de').trim()}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {error ? (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ) : null}

            {/* Eine eigene Höhe, weil der Trenner eine zu verteilen braucht: In einem Elternteil
                ohne Höhe fiele die Ansicht auf null zusammen. */}
            <div className="h-[70vh] min-h-[28rem] min-w-0">
                {resolving ? (
                    /*
                     * Solange der Raum aufgelöst wird, steht der Hinweis **an seiner Stelle**.
                     *
                     * Darunter wäre er unter einer siebzig Prozent hohen Fläche kaum zu sehen, und
                     * der Raum selbst zeigte derweil seine Auskunft für „kein Ordner" — eine
                     * Aussage über die Ablage, wo es nur eine über das Warten zu machen gibt.
                     */
                    <div className="flex h-full items-center justify-center rounded-lg border text-sm text-muted-foreground">
                        {t('portfolioDashboard.documents.preparing')}
                    </div>
                ) : (
                <DataRoomView
                    folderPath={folderPath}
                    orientation="horizontal"
                    embedded
                    onChanged={() => documentImport.reload()}
                    onRemoveAnalysis={(analysisId) => documentImport.remove(analysisId)}
                    onAnalyse={(files, path) => documentImport.upload(files, path)}
                    pending={documentImport.pending}
                    progress={documentImport.progress}
                    decisions={documentImport.decisions}
                    onDecide={setOpenDecision}
                    awaitingMerge={documentImport.awaitingMerge}
                    onMerge={(analysis) =>
                        setOpenMerge({
                            analysisId: analysis._id,
                            documentName: analysis.document?.fileName ?? '',
                            summary: analysis.mergeSummary,
                        })
                    }
                    missing={missing}
                />
                )}
            </div>

            <DecisionDialog
                subject={
                    openDecision
                        ? {
                              extractedObjectId: openDecision.extractedObjectId,
                              term: openDecision.term,
                              className: openDecision.className,
                              candidates: openDecision.candidates as any,
                              searchedKey: openDecision.searchedKey,
                              searchedValues: openDecision.searchedValues,
                              documentName: openDecision.documentName,
                          }
                        : null
                }
                onClose={() => setOpenDecision(null)}
                onDecide={async (_subject, candidate) => {
                    if (!openDecision) return;

                    await documentImport.decide(openDecision, candidate);
                }}
            />

            <MergeDialog
                subject={openMerge}
                onClose={() => setOpenMerge(null)}
                onMerge={(analysisId) => documentImport.merge(analysisId)}
            />
        </div>
    );
}
